import { Fragment, useEffect, useState } from 'react'
import { Box, Collapse, Paper, styled, useTheme } from '@mui/material'
import { Attribute, AttributeLabel, AttributeValue } from 'packages/eid-ui'
import { Button } from './Button'
import { Owners } from './Owners'
import { ArrowIcon } from 'packages/eid-icons'
import { useApplication } from 'hooks'
import { useHistory } from 'react-router'
import { useResourceTypeContext } from 'resourceTypeContext'
import { useTranslation } from 'react-i18next'
import { defaultApplicationLogoImage, getFullImageUrl } from 'utils'
import { Actions } from './Actions'
import { isNilOrEmpty, useQuery } from 'packages/core'
import { Instructions } from './Instructions'
import { Skeleton } from '@mui/material'
import { AzureApplicationOwners } from './AzureApplicationOwners'
import { AzureApplicationDeputies } from './AzureApplicationDeputies'
import appConfig from 'config'
import useSubcomponents from 'useSubcomponents'

const hasAccessToOwnerControl = 'ITShop-Applications-Banner-Owners-Control'
const hasAccessToDeputiesControl = 'ITShop-Applications-Banner-Deputies-Control'
const smallScreenWidth = 960

const StyledSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-start',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    flexWrap: 'wrap',
    width: '100%',
    [`@media (max-width:${smallScreenWidth - 1}px)`]: {
        display: 'none',
    },
}))

const StyledContent = styled(Box)(({ theme }) => ({
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    [`@media (max-width:${smallScreenWidth - 1}px)`]: {
        paddingBottom: theme.spacing(2),
    },
    [`@media (max-width:345px)`]: {
        flexDirection: 'column-reverse',
    },
}))

export const ApplicationBanner = (props: any) => {
    const history = useHistory()
    const query = useQuery()
    const { hasAccessToControl } = useSubcomponents()
    const { applicationId } = props
    const theme = useTheme()
    const { data, isInitialLoading } = useApplication(applicationId)

    const [, dispatch]: any = useResourceTypeContext()

    useEffect(() => {
        if (data)
            dispatch({
                type: 'SET_PROP',
                payload: {
                    key: 'application',
                    value: data,
                },
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data])

    const { t } = useTranslation()

    const [checked, setChecked] = useState(false)

    const handleChange = () => {
        setChecked((prev) => !prev)
    }

    const showAzureApplicationDetails =
        data &&
        data.isAzureApplication &&
        (data.azureApplicationFriendlyName ||
            data.azureApplicationDescription ||
            data.azureApplicationId ||
            data.servicePrincipleId ||
            data.applicationId)

    const azureApplicationDetails = data && (
        <Box>
            <AttributeLabel
                sx={{
                    color: theme?.palette?.common?.black,
                }}
            >
                {t('Common_AzureApplicationDetails')}
            </AttributeLabel>
            <Box display="flex" flexWrap="wrap">
                <Box paddingRight="32px">
                    {data.azureApplicationFriendlyName && (
                        <Attribute>
                            <AttributeLabel>
                                {t('Common_FriendlyName')}
                            </AttributeLabel>
                            <AttributeValue>
                                {data.azureApplicationFriendlyName}
                            </AttributeValue>
                        </Attribute>
                    )}

                    {data.azureApplicationDescription && (
                        <Attribute>
                            <AttributeLabel>
                                {t('Common_Description')}
                            </AttributeLabel>
                            <AttributeValue>
                                {data.azureApplicationDescription}
                            </AttributeValue>
                        </Attribute>
                    )}

                    {data.azureIntegrationType && (
                        <Attribute>
                            <AttributeLabel>
                                {t('Common_Type_Of_Integration')}
                            </AttributeLabel>
                            <AttributeValue>
                                {data.azureIntegrationType}
                            </AttributeValue>
                        </Attribute>
                    )}

                    {data.azureApplicationLine && (
                        <Attribute>
                            <AttributeLabel>
                                {t('Common_Application_Line')}
                            </AttributeLabel>
                            <AttributeValue>
                                {data.azureApplicationLine}
                            </AttributeValue>
                        </Attribute>
                    )}

                    {data.azureUpxId && (
                        <Attribute>
                            <AttributeLabel>
                                {t('Common_Upx_ID')}
                            </AttributeLabel>
                            <AttributeValue>{data.azureUpxId}</AttributeValue>
                        </Attribute>
                    )}

                    {data.azureAuthenticationLevel && (
                        <Attribute>
                            <AttributeLabel>
                                {t('Common_Authentication_Level')}
                            </AttributeLabel>
                            <AttributeValue>
                                {data.azureAuthenticationLevel}
                            </AttributeValue>
                        </Attribute>
                    )}

                    {data.applicationId && (
                        <Attribute>
                            <AttributeLabel>
                                {t('Common_AzureApplicationAppId')}
                            </AttributeLabel>
                            <AttributeValue>
                                {data.applicationId}
                            </AttributeValue>
                        </Attribute>
                    )}

                    {data.azureApplicationId && (
                        <Attribute>
                            <AttributeLabel>
                                {t('Common_AzureApplicationObjectId')}
                            </AttributeLabel>
                            <AttributeValue>
                                {data.azureApplicationId}
                            </AttributeValue>
                        </Attribute>
                    )}

                    {data.servicePrincipleId && (
                        <Attribute>
                            <AttributeLabel>
                                {t('Common_ServicePrincipleId')}
                            </AttributeLabel>
                            <AttributeValue>
                                {data.servicePrincipleId}
                            </AttributeValue>
                        </Attribute>
                    )}
                </Box>

                <Box maxWidth="260px">
                    {data && data.azureApplicationId && (
                        <>
                            {hasAccessToControl(hasAccessToOwnerControl) && (
                                <Box
                                    data-protectedsubcomponent={
                                        hasAccessToOwnerControl
                                    }
                                >
                                    <Attribute>
                                        <AttributeLabel>
                                            {t('Common_Owners')}
                                        </AttributeLabel>
                                        <AzureApplicationOwners data={data} />
                                    </Attribute>
                                </Box>
                            )}
                        </>
                    )}

                    {data && data.azureApplicationId && (
                        <>
                            {hasAccessToControl(hasAccessToDeputiesControl) && (
                                <Box
                                    data-protectedsubcomponent={
                                        hasAccessToDeputiesControl
                                    }
                                >
                                    <Attribute>
                                        <AttributeLabel>
                                            {t('Common_Deputies')}
                                        </AttributeLabel>
                                        <AzureApplicationDeputies data={data} />
                                    </Attribute>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    )

    const applicationDetails = (
        <Fragment>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Attribute>
                    <AttributeLabel>{t('Common_FriendlyName')}</AttributeLabel>
                    <AttributeValue>{data && data.friendlyName}</AttributeValue>
                </Attribute>

                <Attribute>
                    <AttributeLabel>{t('Common_TechnicalName')}</AttributeLabel>
                    <AttributeValue>{data && data.name}</AttributeValue>
                </Attribute>

                <Attribute>
                    <AttributeLabel>{t('Common_Description')}</AttributeLabel>
                    <AttributeValue>{data && data.description}</AttributeValue>
                </Attribute>
            </Box>
        </Fragment>
    )

    const renderContent = () => (
        <Fragment>
            <StyledContent>
                <StyledSection>{applicationDetails}</StyledSection>
                {data && !data.isAzureApplication && (
                    <>
                        {hasAccessToControl(hasAccessToOwnerControl) && (
                            <StyledSection
                                data-protectedsubcomponent={
                                    hasAccessToOwnerControl
                                }
                            >
                                <Attribute>
                                    <AttributeLabel>
                                        {t('Common_Owners')}
                                    </AttributeLabel>
                                    <Owners applicationId={applicationId} />
                                </Attribute>
                            </StyledSection>
                        )}
                    </>
                )}
                {showAzureApplicationDetails && (
                    <StyledSection>{azureApplicationDetails}</StyledSection>
                )}
            </StyledContent>
        </Fragment>
    )

    const renderActionContent = () => (
        <Fragment>
            <Box
                sx={{
                    '& >button': {
                        '&:hover': {
                            backgroundColor: theme?.palette?.primary?.main,
                            color: theme?.palette?.common?.white,
                            '& span': {
                                color: theme?.palette?.common?.white,
                            },
                        },
                    },
                }}
            >
                <Button
                    onClick={() => {
                        query.delete('applicationId')
                        history.push(
                            `${appConfig.APP_SUBPATH}/applications`,
                            query.toString(),
                        )
                    }}
                >
                    {t('Common_BackTo', { text: t('Common_Applications') })}
                </Button>
            </Box>

            {data.instructions && (
                <Box
                    sx={{
                        marginTop: theme.spacing(3),
                        '& >button': {
                            backgroundColor: theme?.palette?.grey?.[900],
                            color: theme?.palette?.common?.white,

                            border: `solid 1px ${theme?.palette?.grey?.[900]}`,

                            '&:hover': {
                                backgroundColor: theme?.palette?.grey?.[900],

                                '& span': {
                                    //  color: theme?.palette?.common?.white,
                                },
                            },
                        },
                    }}
                >
                    <Instructions
                        title={data.friendlyName}
                        instructions={data.instructions}
                    />
                </Box>
            )}

            <Box
                sx={{
                    marginTop: theme.spacing(3),
                }}
            >
                <Actions application={data} />
            </Box>
        </Fragment>
    )

    return (
        <Paper
            sx={{
                position: 'relative',
                padding: theme.spacing(2),
                display: 'flex',
                miHeight: '156px',
                justifyContent: 'space-between',

                [`@media (max-width:${smallScreenWidth - 1}px)`]: {
                    flexDirection: 'column',
                },
            }}
        >
            {isInitialLoading ? (
                <Box width={'100%'}>
                    <Skeleton height={40} />
                </Box>
            ) : (
                <Fragment>
                    <StyledContent>
                        <Paper
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                minWidth: '230px',
                                maxWidth: '230px',

                                '& img': {
                                    maxHeight: '92px',
                                    maxWidth: '146px',
                                },

                                [`@media (max-width:${
                                    smallScreenWidth - 1
                                }px)`]: {
                                    minWidth: '116px',
                                    '& img': {
                                        maxHeight: '33px',
                                        width: '66px',
                                    },
                                },
                            }}
                        >
                            <img
                                alt={data?.friendlyName}
                                src={
                                    isNilOrEmpty(data.applicationLogoImageUrl)
                                        ? getFullImageUrl(
                                              defaultApplicationLogoImage,
                                          )
                                        : getFullImageUrl(
                                              data?.applicationLogoImageUrl,
                                          )
                                }
                            />
                        </Paper>

                        <StyledSection>{renderContent()}</StyledSection>

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                                minWidth: '230px',
                                maxWidth: '230px',
                                paddingRight: theme.spacing(2),

                                [`@media (max-width:445px)`]: {
                                    alignItems: 'flex-start',
                                },
                            }}
                        >
                            {renderActionContent()}
                        </Box>
                    </StyledContent>
                    <Collapse in={checked} collapsedSize={0}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                [`@media (min-width:${smallScreenWidth}px)`]: {
                                    display: 'none',
                                },
                                '& div': {
                                    textAlign: 'start',
                                },
                            }}
                        >
                            {renderContent()}
                        </Box>
                    </Collapse>
                    <Box
                        sx={{
                            [`@media (min-width:${smallScreenWidth}px)`]: {
                                display: 'none',
                            },

                            position: 'absolute',
                            bottom: '-15px',
                            left: 'calc(50% - 83px)',
                            zIndex: 2,
                            '& > button': {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor:
                                    theme?.palette?.background?.paper,
                                border: 'none',
                                height: '32px',
                                minWidth: '115px',
                                color: theme?.palette?.primary?.main,
                                fontSize: '11px',
                                textTransform: 'uppercase',
                                borderRadius: '15px',
                                boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.11)',
                                cursor: 'pointer',
                                paddingLeft: '12px',
                                paddingRight: '6px',
                                '& svg': {
                                    color: theme?.palette?.primary?.main,
                                    height: '20px',
                                },
                            },
                        }}
                    >
                        <button
                            onClick={handleChange}
                            aria-expanded={checked}
                        >
                            {checked ? t('Common_Hide') : t('Common_ShowMore')}
                            <ArrowIcon direction={checked ? 'up' : 'down'} />
                        </button>
                    </Box>
                </Fragment>
            )}
        </Paper>
    )
}
